import { type JSX, useEffect, useState } from 'react';

import { anlegen, liste, type Kundenart, type Kundenzeile } from '../api/kunden';
import { ApiFehler } from '../api/fehler';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';

/**
 * Den Kunden einer neuen Buchung wählen (BUC-80; Auftraggeber, 19.09.2026).
 *
 * „Im Formular soll sich bei Namen … eine Suchmaske öffnen."
 *
 * Gesucht wird über die Kundensuche des Hauses (KUN-22): Name, Nummer, Ort,
 * E-Mail, fehlertolerant — ab zwei Zeichen, getaktet. Die Kundenart steht als
 * eigenes Feld daneben, weil sie am Telefon oft das Erste ist, was feststeht
 * („eine Firma aus Hannover").
 *
 * ============================================================================
 * WER NOCH KEIN KUNDE IST, WIRD HIER EINER (BUC-80)
 * ============================================================================
 *
 * BUC-80 verlangt die Aufnahme „ohne Wechsel zwischen Modulen". Ein Verweis
 * auf die Kundenverwaltung waere genau dieser Wechsel — mitten im Gespraech,
 * mit verlorener Terminwahl. Angelegt wird deshalb hier, mit dem Wenigen, was
 * am Telefon feststeht: Art, Name, ein Kanal. Alles Weitere — Anschrift,
 * Kondition, USt-IdNr. — steht in der Kundenakte und wird dort gepflegt
 * (KUN-01, KUN-04).
 */

const ARTNAME: Readonly<Record<Kundenart, string>> = {
  privat: 'Privat',
  geschaeft: 'Geschäft',
  oeffentlich: 'Öffentlich',
};

export function Kundensuche({
  beiWahl,
  beiSchliessen,
}: {
  readonly beiWahl: (kunde: Kundenzeile) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [suche, setzeSuche] = useState('');
  const [art, setzeArt] = useState<Kundenart | ''>('');
  const [kunden, setzeKunden] = useState<readonly Kundenzeile[]>([]);
  const [mehr, setzeMehr] = useState(false);
  const [laeuft, setzeLaeuft] = useState(false);
  /* BUC-80: die Anlage im selben Fenster. */
  const [anlage, setzeAnlage] = useState(false);
  const [nArt, setzeNArt] = useState<Kundenart>('privat');
  const [nVorname, setzeNVorname] = useState('');
  const [nNachname, setzeNNachname] = useState('');
  const [nFirmierung, setzeNFirmierung] = useState('');
  const [nEmail, setzeNEmail] = useState('');
  const [nTelefon, setzeNTelefon] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [legtAn, setzeLegtAn] = useState(false);

  const nameFehlt = nArt === 'privat' ? nNachname.trim() === '' : nFirmierung.trim() === '';

  const lege = async (): Promise<void> => {
    setzeLegtAn(true);
    setzeFehler(null);
    try {
      const kanaele = [
        ...(nEmail.trim() === ''
          ? []
          : [{ art: 'email' as const, wert: nEmail.trim(), bezeichnung: null }]),
        ...(nTelefon.trim() === ''
          ? []
          : [{ art: 'telefon' as const, wert: nTelefon.trim(), bezeichnung: null }]),
      ];
      const neu = await anlegen({
        kundenart: nArt,
        firmierung: nArt === 'privat' ? null : nFirmierung.trim(),
        person:
          nArt === 'privat'
            ? {
                anrede: null,
                titel: null,
                vorname: nVorname.trim() === '' ? null : nVorname.trim(),
                nachname: nNachname.trim(),
                geburtsdatum: null,
              }
            : null,
        rechtsform: null,
        ustidnr: null,
        leitwegId: null,
        adresse: null,
        kanaele,
        ansprechpartner: null,
      });
      /*
       * DIE ZEILE KOMMT VOM SERVER und wird nicht hier zusammengesetzt:
       * Kundennummer, Status und Betreuer vergibt er, nicht die Maske.
       */
      const gefunden = (await liste({ suche: neu.kundennummer })).kunden.find(
        (k) => k.id === neu.id,
      );
      if (gefunden !== undefined) beiWahl(gefunden);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLegtAn(false);
    }
  };

  useEffect(() => {
    if (suche.trim().length < 2) {
      setzeKunden([]);
      setzeMehr(false);
      return;
    }
    let abgebrochen = false;
    setzeLaeuft(true);
    const uhr = setTimeout(() => {
      liste({ suche: suche.trim(), ...(art === '' ? {} : { kundenarten: [art] }) })
        .then((g) => {
          if (abgebrochen) return;
          setzeKunden(g.kunden);
          setzeMehr(g.mehrAlsGezeigt);
        })
        .catch(() => {
          if (!abgebrochen) setzeKunden([]);
        })
        .finally(() => {
          if (!abgebrochen) setzeLaeuft(false);
        });
    }, 250);
    return () => {
      abgebrochen = true;
      clearTimeout(uhr);
    };
  }, [suche, art]);

  return (
    <Dialog titel="Kunde auswählen" breite="breit" beiSchliessen={beiSchliessen}>
      <div className="suchmaske__felder">
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="ks-suche">
            Name, Kundennummer, Ort oder E-Mail
          </label>
          <input
            id="ks-suche"
            className="feld__eingabe"
            value={suche}
            onChange={(e) => setzeSuche(e.target.value)}
          />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="ks-art">
            Kundenart
          </label>
          <select
            id="ks-art"
            className="feld__eingabe"
            value={art}
            onChange={(e) => setzeArt(e.target.value as Kundenart | '')}
          >
            <option value="">jede</option>
            {Object.entries(ARTNAME).map(([wert, text]) => (
              <option key={wert} value={wert}>
                {text}
              </option>
            ))}
          </select>
        </div>
      </div>

      {fehler !== null && <Meldung fehler={fehler} />}

      {anlage ? (
        <section className="karte karte--zweit" aria-label="Neuer Kunde">
          <h3>Neuen Kunden anlegen</h3>
          <p className="feld__hilfe">
            Nur das Nötige für die Buchung. Anschrift, Kondition und USt-IdNr. werden danach in der
            Kundenakte gepflegt.
          </p>
          <div className="suchmaske__felder">
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ks-neu-art">
                Kundenart
              </label>
              <select
                id="ks-neu-art"
                className="feld__eingabe"
                value={nArt}
                onChange={(e) => setzeNArt(e.target.value as Kundenart)}
              >
                {Object.entries(ARTNAME).map(([wert, text]) => (
                  <option key={wert} value={wert}>
                    {text}
                  </option>
                ))}
              </select>
            </div>
            {nArt === 'privat' ? (
              <>
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="ks-neu-vorname">
                    Vorname
                  </label>
                  <input
                    id="ks-neu-vorname"
                    className="feld__eingabe"
                    value={nVorname}
                    onChange={(e) => setzeNVorname(e.target.value)}
                  />
                </div>
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="ks-neu-nachname">
                    Nachname <span className="feld__pflicht">Pflicht</span>
                  </label>
                  <input
                    id="ks-neu-nachname"
                    className="feld__eingabe"
                    value={nNachname}
                    onChange={(e) => setzeNNachname(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="ks-neu-firmierung">
                  Firmierung <span className="feld__pflicht">Pflicht</span>
                </label>
                <input
                  id="ks-neu-firmierung"
                  className="feld__eingabe"
                  value={nFirmierung}
                  onChange={(e) => setzeNFirmierung(e.target.value)}
                />
              </div>
            )}
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ks-neu-email">
                E-Mail
              </label>
              <input
                id="ks-neu-email"
                type="email"
                className="feld__eingabe"
                value={nEmail}
                onChange={(e) => setzeNEmail(e.target.value)}
              />
            </div>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ks-neu-telefon">
                Telefon
              </label>
              <input
                id="ks-neu-telefon"
                className="feld__eingabe"
                value={nTelefon}
                onChange={(e) => setzeNTelefon(e.target.value)}
              />
            </div>
          </div>
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              onClick={() => {
                setzeAnlage(false);
                setzeFehler(null);
              }}
            >
              Zurück zur Suche
            </button>
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={nameFehlt || legtAn}
              onClick={() => void lege()}
            >
              Anlegen und übernehmen
            </button>
          </div>
        </section>
      ) : suche.trim().length < 2 ? (
        <p className="feld__hilfe">Ab zwei Zeichen wird gesucht.</p>
      ) : laeuft ? (
        <p className="feld__hilfe" aria-busy="true">
          wird gesucht …
        </p>
      ) : kunden.length === 0 ? (
        <p className="liste__leer">Kein Kunde zu dieser Suche.</p>
      ) : (
        <>
          {mehr && <p className="feld__hilfe">Es gibt mehr Treffer — verengen Sie die Suche.</p>}
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Gefundene Kunden">
              <thead>
                <tr>
                  <th scope="col">Kunde</th>
                  <th scope="col">Art</th>
                  <th scope="col">Ort</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {kunden.map((k) => (
                  <tr key={k.id}>
                    <td>
                      <button type="button" className="knopf knopf--verweis" onClick={() => beiWahl(k)}>
                        {k.kundennummer} — {k.name}
                      </button>
                    </td>
                    <td>{ARTNAME[k.kundenart]}</td>
                    <td>{k.ort ?? '—'}</td>
                    <td>
                      {k.gesperrt ? <span className="marke marke--warn">gesperrt</span> : k.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!anlage && (
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf"
            onClick={() => {
              setzeAnlage(true);
              /* Was getippt wurde, ist meistens der Name — er wandert mit. */
              if (suche.trim() !== '') {
                setzeNNachname(suche.trim());
                setzeNFirmierung(suche.trim());
              }
            }}
          >
            Neuen Kunden anlegen
          </button>
        </div>
      )}
    </Dialog>
  );
}
